import { getRequestHeaders } from './api.utils';
import { ToasterContext } from '../toaster/toaster.provider';
import { UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { UNIQUE_ID } from '../utils';
import api from './api';
import { API_HOST_URL } from './api.const';
import type { BusinessUserAccessTokenResponse } from './types';

export const useChangePasswordRequest = () => {
  const headers = getRequestHeaders();

  return [
    async (password: string) => {
      const [response] = await api(
        `${API_HOST_URL}/update-password`,
        { method: 'POST', body: { password, confirmPassword: password } },
        headers,
      );

      return response;
    },
  ];
};

export const useDownloadBillingInvoice = () => {
  const headers = getRequestHeaders({ ignoreActingUser: true });
  const { error } = useContext(ToasterContext);

  return useMutation({
    onSuccess: (response, transactionId) => {
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${transactionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    onError: (err, variables, context) => {
      error({ message: err.message });
    },
    mutationFn: async (invoiceId: number) => {
      const [response] = await api<Blob>(
        `${API_HOST_URL}/billing/download/${invoiceId}`,
        { method: 'POST', responseType: 'blob' },
        headers,
      );

      return response;
    },
  });
};

export const useChangeBusinessUserPasswordRequest = () => {
  const headers = getRequestHeaders();

  return [
    async (userId: number, password: string) => {
      const [response] = await api(
        `${API_HOST_URL}/business/users/${userId}/change-password`,
        { method: 'POST', body: { password } },
        headers,
      );

      return response;
    },
  ];
};

export const useChangeTariffRequest = () => {
  const headers = getRequestHeaders();

  return [
    async (tariffId: number) => {
      const [response] = await api(
        `${API_HOST_URL}/me/change-tariff`,
        { method: 'POST', body: { tariffId } },
        headers,
      );

      return response;
    },
  ];
};

export const useCancelTariffRenewalRequest = () => {
  const headers = getRequestHeaders();

  return [
    async () => {
      const [response] = await api(
        `${API_HOST_URL}/me/cancel-renewal`,
        { method: 'POST', body: {} },
        headers,
      );

      return response;
    },
  ];
};

export const useCompleteProfileRequest = () => {
  const headers = getRequestHeaders();

  return [
    async (body: { type: string; region: string }) => {
      const [response] = await api(
        `${API_HOST_URL}/api/setup-camone`,
        { method: 'POST', body },
        headers,
      );

      return response;
    },
  ];
};

export const useStoreChaportIdRequest = () => {
  const headers = getRequestHeaders();

  return [
    async (body: { id: string }) => {
      const [response] = await api(`${API_HOST_URL}/chaport-id`, { method: 'POST', body }, headers);

      return response;
    },
  ];
};

export const useBusinessUserTokenRequest = (args: {
  adminRequest?: boolean;
  tokenType: 'overlays' | 'server' | 'chats';
}) => {
  const { adminRequest = false, tokenType } = args;
  const headers = getRequestHeaders({ ignoreActingUser: true });

  const urlType = adminRequest ? 'admin' : 'business';
  const instanceId = `${tokenType === 'chats' ? 'C_' : 'M_'}${UNIQUE_ID}`;

  return [
    async (id: number, login: string) => {
      const [response] = await api<BusinessUserAccessTokenResponse>(
        `${API_HOST_URL}/${urlType}/users/${
          adminRequest ? id : login || id
        }/token?iid=${instanceId}&tokenType=${tokenType}`,
        { method: 'GET' },
        headers,
      );

      return response;
    },
  ];
};

export const useMigrateToBusinessMutation = (opts?: Omit<UseMutationOptions, 'mutationFn'>) => {
  const headers = getRequestHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    onSuccess: (d, v, c) => {
      queryClient.invalidateQueries({ queryKey: ['me'] });

      opts?.onSuccess?.(d, v, c);
    },
    mutationFn: async () => {
      const [response] = await api(
        `${API_HOST_URL}/me/upgrade-business`,
        { method: 'POST' },
        headers,
      );

      return response;
    },
  });
};

export const useAddBusinessUserMutation = () => {
  const headers = getRequestHeaders();
  const { error } = useContext(ToasterContext);
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business/users'] });
    },
    onError: (err, variables, context) => {
      error({ message: err.message });
    },
    mutationFn: async (body: { login: string; password: string; tariffId: number }) => {
      const [response] = await api(
        `${API_HOST_URL}/business/users`,
        { method: 'POST', body },
        headers,
      );

      return response;
    },
  });
};

export const useSaveManagerSettingsMutation = () => {
  const headers = getRequestHeaders();
  const { error } = useContext(ToasterContext);
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (d, v) => {
      queryClient.invalidateQueries({ queryKey: ['business/users'] });
      queryClient.invalidateQueries({
        queryKey: [`business/managers/${v.managerId}/settings`],
      });
    },
    onError: err => {
      error({ message: err.message });
    },
    mutationFn: async ({
      managerId,
      permissions,
      streamerIds,
    }: {
      managerId: number;
      permissions: string[];
      streamerIds: number[];
    }) => {
      const [response] = await api(
        `${API_HOST_URL}/business/managers/${managerId}/settings`,
        { method: 'PUT', body: { permissions, streamerIds } },
        headers,
      );

      return response;
    },
  });
};

export const useDeleteBusinessUserMutation = () => {
  const headers = getRequestHeaders();
  const { error } = useContext(ToasterContext);
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business/users'] });
    },
    onError: (err, variables, context) => {
      error({ message: err.message });
    },
    mutationFn: async (userId: number) => {
      const [response] = await api(
        `${API_HOST_URL}/business/users/${userId}`,
        { method: 'DELETE' },
        headers,
      );

      return response;
    },
  });
};

export const useRedeemCodeMutation = () => {
  const headers = getRequestHeaders();
  const { error, success } = useContext(ToasterContext);
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tariffs'] });

      success({ message: 'Code has been redeemed successfully' });
    },
    onError: err => {
      error({ message: err.message });
    },
    mutationFn: async (code: string) => {
      const [response] = await api(
        `${API_HOST_URL}/me/redeem-code`,
        { method: 'POST', body: { code } },
        headers,
      );

      return response;
    },
  });
};

export const useChangeBusinessTariffRequest = () => {
  const headers = getRequestHeaders();
  const { error } = useContext(ToasterContext);
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (d, v, c) => {
      queryClient.invalidateQueries({ queryKey: ['business/users'] });
    },
    onError: err => {
      error({ message: err.message });
    },
    mutationFn: async ({ userId, tariffId }: { userId: number; tariffId: number }) => {
      const [response] = await api(
        `${API_HOST_URL}/business/users/${userId}/change-tariff`,
        { method: 'POST', body: { tariffId } },
        headers,
      );

      return response;
    },
  });
};

export const useBillingPortalUrlRequest = (opts?: Omit<UseMutationOptions, 'mutationFn'>) => {
  const headers = getRequestHeaders();
  const { error } = useContext(ToasterContext);

  return useMutation({
    ...opts,
    onSuccess: (d, v, c) => {
      opts?.onSuccess?.(d, v, c);
    },
    onError: (err: any, variables, context) => {
      if (opts?.onError) {
        return opts.onError(err, variables, context);
      }

      error({ message: err.message });
    },
    mutationFn: async () => {
      const [response] = await api(
        `${API_HOST_URL}/me/billing-portal-url`,
        { method: 'GET' },
        headers,
      );

      return response as { data: { url: string } };
    },
  });
};

export const useTopUpWithCryptoRequest = () => {
  const headers = getRequestHeaders();
  const { error } = useContext(ToasterContext);

  return useMutation({
    onError: (err: any) => {
      error({ message: err.message });
    },
    mutationFn: async (value: number) => {
      const [response] = await api(
        `${API_HOST_URL}/coingate/topup`,
        { method: 'POST', body: { value } },
        headers,
      );

      return response as { data: { redirect: string } };
    },
  });
};

export const useChangeTariffWithCryptoRequest = () => {
  const headers = getRequestHeaders();
  const { error } = useContext(ToasterContext);

  return useMutation({
    onError: (err: any) => {
      error({ message: err.message });
    },
    mutationFn: async (tariffId: number) => {
      const [response] = await api(
        `${API_HOST_URL}/coingate/tariff`,
        { method: 'POST', body: { tariffId } },
        headers,
      );

      return response as { data: { redirect: string } };
    },
  });
};

export const useWithdrawalRequestMutation = () => {
  const headers = getRequestHeaders();
  const { error } = useContext(ToasterContext);
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (d, v, c) => {
      if (v.type === 'TRANSFER') {
        queryClient.invalidateQueries({ queryKey: ['me/referral-stats'] });
        queryClient.invalidateQueries({ queryKey: ['me'] });
      }
    },
    onError: (err: any) => {
      error({ message: err.message });
    },
    mutationFn: async (body: { value: number; type: 'WITHDRAWAL' | 'TRANSFER' }) => {
      const [response] = await api(
        `${API_HOST_URL}/me/request-withdrawal`,
        { method: 'POST', body },
        headers,
      );

      return response;
    },
  });
};

export const useCompleteProfileMutation = () => {
  const headers = getRequestHeaders();
  const { error } = useContext(ToasterContext);
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (d, v, c) => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err: any) => {
      error({ message: err.message });
    },
    mutationFn: async (body: any) => {
      const [response] = await api(
        `${API_HOST_URL}/me/complete`,
        { method: 'POST', body },
        headers,
      );

      return response;
    },
  });
};

export const useCreateSupportTicketMutation = () => {
  const headers = getRequestHeaders();
  const { error } = useContext(ToasterContext);

  return useMutation({
    onError: (err: any) => {
      error({ message: err.message });
    },
    mutationFn: async (body: FormData) => {
      const [response] = await api<{ message: string; ticketId: number }>(
        `${API_HOST_URL}/me/tickets`,
        { method: 'POST', body },
        headers,
      );

      return response;
    },
  });
};

export const useSendReplyToSupportTicket = () => {
  const queryClient = useQueryClient();
  const headers = getRequestHeaders();
  const { error } = useContext(ToasterContext);

  return useMutation({
    onError: (err: any) => {
      error({ message: err.message });
    },
    onSuccess: (d, v) => {
      queryClient.invalidateQueries({ queryKey: [`me/tickets/${v.id}`] });
    },
    mutationFn: async ({ id, body }: { id: string | number; body: FormData }) => {
      const [response] = await api<{ message: string; ticketId: number }>(
        `${API_HOST_URL}/me/tickets/${id}/reply`,
        { method: 'POST', body },
        headers,
      );

      return response;
    },
  });
};
